import { useCallback, useEffect, useRef, useState } from 'react';
import { Tabs } from '../components/common/Tabs';
import PageHeader from '../components/common/PageHeader';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import BeatSetupPanel from '../components/news-beats/BeatSetupPanel';
import BeatPublishPanel from '../components/news-beats/BeatPublishPanel';
import BriefingView from '../components/news-beats/BriefingView';
import RunInspector from '../components/news-beats/RunInspector';
import BeatAgentPane from '../components/news-beats/BeatAgentPane';
import BeatVerdictStrip from '../components/news-beats/BeatVerdictStrip';
import ThreadsPanel from '../components/news-beats/ThreadsPanel';
import StageModelsWidget from '../components/common/StageModelsWidget';
import { useWebSocket } from '../contexts/WebSocketContext';
import api from '../services/api';
import type {
  NewsBeatDetail,
  NewsBriefingDetail,
  NewsBriefingSummary,
} from '../types';

/**
 * One beat: Setup, its Briefings, and the Threads it is tracking.
 *
 * Plain tabs rather than SettingsLayout — that shell is built for a sidebar of
 * many small sections, and each of these three is a full screen in its own right.
 */

type Section = 'setup' | 'briefings' | 'threads';

const SECTIONS: { key: Section; label: string }[] = [
  { key: 'setup', label: 'Setup' },
  { key: 'briefings', label: 'Briefings' },
  { key: 'threads', label: 'Tracked stories' },
];

const STATUS_STYLE: Record<string, string> = {
  QUEUED: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  RUNNING: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 animate-pulse',
  COMPLETED: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
  FAILED: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  CANCELLED: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
};

export default function NewsBeat() {
  const { beatId, section } = useParams<{ beatId: string; section?: string }>();
  const navigate = useNavigate();
  const { subscribe } = useWebSocket();
  const id = Number(beatId);

  const active: Section = (SECTIONS.find((s) => s.key === section)?.key ??
    'setup') as Section;

  const [beat, setBeat] = useState<NewsBeatDetail | null>(null);
  const [briefings, setBriefings] = useState<NewsBriefingSummary[]>([]);
  const [selected, setSelected] = useState<NewsBriefingDetail | null>(null);
  /**
   * Which briefing is open lives in the URL.
   *
   * It was component state, so a briefing could not be linked to at all — and
   * the artifact shelf, whose whole job is "what have I got and where is it",
   * could only get you as far as the beat's list and leave you to find the one
   * you clicked among twenty.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const briefingParam = Number(searchParams.get('briefing')) || null;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // What the beat's most recent briefing is doing, straight from the server.
  // A boolean was not enough: a job that dies before stamping `started_at`
  // leaves its row QUEUED, and calling that "Running…" is a small lie about a
  // run that never started.
  const [runState, setRunState] = useState<'idle' | 'queued' | 'running'>('idle');
  // What the agent is doing right now, streamed while a briefing builds. A run
  // is minutes of silence otherwise, and silence reads as a hang.
  const [activity, setActivity] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  // Which briefing the run inspector is showing, or null when it is closed.
  // A briefing id rather than a boolean: the panel is opened from two places
  // about two different runs (the latest, from Setup; the selected one, from
  // Briefings) and a flag would make "which run" implicit.
  const [inspecting, setInspecting] = useState<number | null>(null);
  // An opening line handed to the agent by a verdict chip.
  const [seed, setSeed] = useState<string | null>(null);
  // Bumped whenever the settings or a run change, so the verdict re-reads.
  const [verdictKey, setVerdictKey] = useState(0);

  const askTuner = useCallback((question: string) => {
    setSeed(question);
    setChatOpen(true);
  }, []);

  const loadBeat = useCallback(async () => {
    try {
      setBeat(await api.getNewsBeat(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadBriefings = useCallback(async () => {
    try {
      const rows = await api.listNewsBriefings(id);
      setBriefings(rows);
      const live = rows.find((r) => r.status === 'QUEUED' || r.status === 'RUNNING');
      setRunState(
        live ? (live.status === 'RUNNING' ? 'running' : 'queued') : 'idle',
      );
      return rows;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return [];
    }
  }, [id]);

  const openBriefing = useCallback(
    async (briefingId: number, { push = true }: { push?: boolean } = {}) => {
      try {
        setSelected(await api.getNewsBriefing(briefingId));
        // `push: false` for the refreshes that re-open what is already showing
        // — a websocket tick should not rewrite the URL.
        if (push) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set('briefing', String(briefingId));
              return next;
            },
            { replace: true },
          );
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [setSearchParams],
  );

  // A trailing debounce over the story/muted stream. The briefing GET returns
  // every story, rejection and unreviewed article, so firing one per event
  // meant a 25-story run made 25 full-briefing requests during the minute it
  // was busiest. Trailing rather than leading: the interesting version is
  // always the latest one.
  const refreshTimer = useRef<number | null>(null);
  // WHAT A RUN CHANGES WHILE YOU WATCH, beyond the briefing open: the tracked
  // stories and the inspector's own lists. Bunched to one re-read every few
  // seconds, the way the briefing's own refresh is.
  const [liveKey, setLiveKey] = useState(0);
  const liveTimer = useRef<number | null>(null);
  const bumpLive = useCallback(() => {
    if (liveTimer.current) return;
    liveTimer.current = window.setTimeout(() => {
      liveTimer.current = null;
      setLiveKey((k) => k + 1);
    }, 5_000);
  }, []);
  useEffect(
    () => () => {
      if (liveTimer.current) window.clearTimeout(liveTimer.current);
    },
    [],
  );
  const scheduleBriefingRefresh = useCallback(
    (briefingId: number) => {
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
      refreshTimer.current = window.setTimeout(() => {
        refreshTimer.current = null;
        openBriefing(briefingId);
      }, 1500);
    },
    [openBriefing],
  );
  useEffect(
    () => () => {
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    },
    [],
  );

  useEffect(() => {
    loadBeat();
  }, [loadBeat]);

  // Runs on every tab, not just Briefings. It used to be gated, which made the
  // run state a flag that could only ever go UP on Setup: switch to Briefings
  // while a briefing is queued, come back, and the button says "Running…"
  // forever — the only thing that cleared it was a `finished` event, which
  // never arrives for a job that died.
  useEffect(() => {
    loadBriefings().then((rows) => {
      // Open the most recent briefing on arrival — it is what the user came for.
      if (active !== 'briefings' || !rows.length || selected) return;
      // The URL wins over "most recent" — arriving from the shelf names the
      // briefing you clicked, and defaulting past it is how a link stops
      // meaning anything.
      const wanted = rows.some((r) => r.id === briefingParam) ? briefingParam! : rows[0].id;
      openBriefing(wanted);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, loadBriefings, openBriefing]);

  // Live updates while a briefing builds.
  useEffect(() => {
    return subscribe((event: any) => {
      if (event?.event_type !== 'news_beat' || event.beat_id !== id) return;
      if (
        event.status === 'activity' ||
        event.status === 'started' ||
        event.status === 'progress'
      ) {
        if (event.message) setActivity(event.message);
        setRunState('running');
      }
      // A RUN THE SCHEDULER STARTS has no row in the list until it is read
      // again: re-read on its start, so it appears and its pill moves.
      if (event.status === 'started') loadBriefings();
      if (['story', 'muted', 'rejected', 'progress'].includes(event.status)) bumpLive();
      // `rejected` was previously ignored, so a run spent entirely on filtering
      // looked idle. It carries no message worth showing, but it is proof the
      // agent is working.
      if (event.status === 'rejected') setRunState('running');
      if (event.status === 'story' || event.status === 'muted') {
        setActivity(event.message ?? null);
        // DEBOUNCED. Each of these used to trigger a full briefing GET, so a
        // 25-story run fired 25 of them at the busiest moment of the run.
        if (selected?.id === event.briefing_id) {
          scheduleBriefingRefresh(event.briefing_id);
        }
      }
      if (['finished', 'failed', 'cancelled'].includes(event.status)) {
        setActivity(null);
        setRunState('idle');
        // Bumped so the verdict strip AND whatever the inspector is showing
        // re-read the run that just landed.
        setVerdictKey((k) => k + 1);
        setLiveKey((k) => k + 1);
        loadBriefings();
        // The beat itself moved too: "Last scheduled run" on Setup.
        loadBeat();
        if (selected?.id === event.briefing_id) openBriefing(event.briefing_id, { push: false });
      }
    });
  }, [
    subscribe,
    id,
    selected,
    openBriefing,
    loadBriefings,
    loadBeat,
    bumpLive,
    scheduleBriefingRefresh,
  ]);

  // Both run triggers stay on THIS tab and open the inspector rather than
  // navigating away. The point of the redesign is that you change a setting and
  // watch what it does without losing sight of the setting.
  const run = async () => {
    setError(null);
    try {
      const res = await api.runNewsBeat(id);
      setRunState('queued');
      setActivity('Searching for articles…');
      setInspecting(res.briefing_id);
      await loadBriefings();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /** Pick up the articles a run never reached, keeping what it wrote. */
  const continueRun = useCallback(
    async (briefingId: number) => {
      setError(null);
      try {
        const res = await api.continueNewsBriefing(briefingId);
        setRunState('queued');
        setActivity(res.message);
        setInspecting(briefingId);
        await loadBriefings();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [loadBriefings],
  );

  /** Judge the same articles again with the current settings. No fetch. */
  const reprocess = useCallback(
    async (briefingId: number) => {
      setError(null);
      try {
        await api.reprocessNewsBriefing(briefingId);
        setRunState('queued');
        setActivity('Re-judging the articles already fetched…');
        setInspecting(briefingId);
        await loadBriefings();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [loadBriefings],
  );

  const toggleActive = async () => {
    setError(null);
    try {
      setBeat(await api.updateNewsBeat(id, { is_active: !beat?.is_active }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const backToDraft = async () => {
    setError(null);
    // Honest about both halves: nothing goes yet, but re-processing — which
    // does delete — becomes reachable, and the cadence stops meanwhile.
    const ok = window.confirm(
      `Put "${beat?.name}" back into draft?\n\n` +
        `Nothing is deleted now. It stops running on its schedule, and you can ` +
        `then re-judge its articles with new settings - which DOES clear the ` +
        `run's stories and the beat's tracked stories each time.\n\n` +
        `Going live again afterwards clears the draft results.`,
    );
    if (!ok) return;
    try {
      setBeat(await api.returnNewsBeatToDraft(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const goLive = async () => {
    setError(null);
    let preview;
    try {
      preview = await api.getNewsBeatPromotePreview(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return;
    }
    const { briefings: b, stories: s, threads: t } = preview.deletes;
    // Named counts, not "are you sure": the whole point of the preview call is
    // that the confirm can say what it is about to destroy.
    const ok = window.confirm(
      `Going live clears everything this draft produced:\n\n` +
        `  · ${b} briefing${b === 1 ? '' : 's'}\n` +
        `  · ${s} storie${s === 1 ? '' : 's'}\n` +
        `  · ${t} tracked storie${t === 1 ? '' : 's'}\n\n` +
        `The beat then starts running on its schedule with an empty memory, ` +
        `so it will not wrongly mute a story it "already reported" while you ` +
        `were still changing settings.\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    try {
      setBeat(await api.promoteNewsBeat(id));
      await loadBriefings();
      setSelected(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${beat?.name}" and all its briefings?`)) return;
    await api.deleteNewsBeat(id);
    navigate('/news-beats');
  };

  if (loading) {
    return <div className="p-6 text-gray-500 dark:text-gray-400">Loading…</div>;
  }
  if (!beat) {
    return (
      <div className="p-6 space-y-2">
        <p className="text-sm text-red-600 dark:text-red-400">
          {error ?? 'Beat not found.'}
        </p>
        <Link to="/news-beats" className="text-sm text-indigo-600 hover:underline">
          Back to News Beats
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* The trail and the title in one band, like every other screen. The
          `← News Beats` link this replaces was a fourth back-link style, and it
          sat above a ReturnTrail chip whenever a brainstorm had deep-linked
          into the beat — two ways out, stacked, in two sizes.

          The state chips read as a subtitle: they say what this beat IS right
          now, directly under its name. They are kept out of the <h1> so a
          screen reader announces the beat's name and not five control labels
          after it. */}
      <PageHeader
        title={beat.name}
        breadcrumb={[{ label: 'News Beats', to: '/news-beats' }, { label: beat.name }]}
        className=""
        right={<StageModelsWidget surface="news_beats" returnLabel="this beat" />}
        subtitle={
          <span className="flex flex-wrap items-center gap-3">
            {/* The cadence switch, and it is a CONTROL rather than a badge.
                It used to be a read-only pill here and a button buried in the
                Setup form, so the state and the way to change it were on two
                different screens.

                Only on a live beat: the scheduler skips drafts outright, and
                `promote` sets `is_active` from its own argument — so "active"
                on a draft would be a switch wired to nothing. */}
            {beat.mode === 'LIVE' && (
              <button
                onClick={toggleActive}
                title={
                  beat.is_active
                    ? 'Running on its schedule. Click to pause it.'
                    : 'Not running. Click to start the schedule.'
                }
                className={`text-xs px-2 py-0.5 rounded-full ${
                  beat.is_active
                    ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/60'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {beat.is_active ? 'Active - click to pause' : 'Paused - click to run'}
              </button>
            )}
            {/* Draft is amber rather than green: it is a state to LEAVE, and a
                beat quietly sitting in it is one that never went live. */}
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                beat.mode === 'DRAFT'
                  ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
              title={
                beat.mode === 'DRAFT'
                  ? 'Still being built. Runs are disposable and can be re-judged, ' +
                    'and the schedule is not firing yet.'
                  : 'Running on its schedule. Its tracked stories are real memory.'
              }
            >
              {beat.mode === 'DRAFT' ? 'Draft' : 'Live'}
            </span>
            {beat.mode === 'DRAFT' ? (
              <button
                onClick={goLive}
                className="text-xs px-2 py-0.5 rounded-full border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                title="Clear the drafts and start running on the schedule"
              >
                Go live
              </button>
            ) : (
              <button
                onClick={backToDraft}
                className="text-xs px-2 py-0.5 rounded-full border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                title="Pause the schedule and re-judge this beat's articles with new settings"
              >
                Tune it
              </button>
            )}
            {activity && (
              <span className="text-xs text-blue-600 dark:text-blue-400 animate-pulse">
                {activity}
              </span>
            )}
          </span>
        }
      />

      {/* The shared strip. This was the app's only INDIGO tab bar — every other
          one was blue — which made a beat look like a different product from
          the screen you had just come from. The `overflow-y-hidden` that used to
          live here moved into the component with the note explaining it. */}
      <Tabs
        tabs={SECTIONS}
        active={active}
        onChange={(key) => navigate(`/news-beats/${id}/${key}`)}
        className=""
      />

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {active === 'setup' && (
        <>
          <div className="max-w-3xl mb-5 space-y-3">
            <div className="flex justify-end gap-2">
              {briefings.length > 0 && (
                <button
                  onClick={() => setInspecting(briefings[0].id)}
                  className="text-sm px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-indigo-400"
                >
                  {runState === 'idle'
                    ? 'How the last run went'
                    : 'Watch this run'}
                </button>
              )}
              <button
                onClick={() => setChatOpen(true)}
                className="text-sm px-3 py-1.5 rounded-md border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
              >
                Tune this beat
              </button>
            </div>
            <BeatVerdictStrip
              beatId={id}
              reloadKey={verdictKey}
              onAsk={askTuner}
            />
          </div>
          <BeatSetupPanel
            beat={beat}
            onSaved={setBeat}
            onRun={run}
            onReprocess={reprocess}
            lastBriefingId={briefings[0]?.id ?? null}
            runState={runState}
          />
          {/* Below the tuning, because what a beat WATCHES is decided long
              before where it publishes — and unlike the fields above, this one
              is not the tuning agent's to change. */}
          <BeatPublishPanel beat={beat} onSaved={setBeat} />
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={remove}
              className="text-sm text-red-600 dark:text-red-400 hover:underline"
            >
              Delete this beat
            </button>
          </div>
        </>
      )}

      {active === 'briefings' && (
        // minmax(0,1fr), NOT 1fr: a `1fr` track has an automatic minimum of its
        // CONTENT, so one unbreakable string — a GNews error carrying a
        // 900-character URL — widens the column past the viewport and nothing
        // inside it can wrap, however many break-words you add.
        <div className="grid gap-5 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <div className="space-y-1">
            {briefings.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No briefings yet. Use <strong>Run now</strong> on the Setup tab.
              </p>
            )}
            {briefings.map((b) => (
              <button
                key={b.id}
                onClick={() => openBriefing(b.id)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                  selected?.id === b.id
                    ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-300 dark:border-indigo-700'
                    : 'border border-transparent hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="text-gray-900 dark:text-gray-100">
                    {new Date(b.created_at).toLocaleString()}
                  </span>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      STATUS_STYLE[b.status] ?? ''
                    }`}
                  >
                    {b.status.toLowerCase()}
                  </span>
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  {b.stories_written} stories
                  {b.updates_written > 0 && ` · ${b.updates_written} updates`}
                  {b.trigger === 'MANUAL' && ' · manual'}
                </span>
              </button>
            ))}
          </div>
          <div className="space-y-4">
            {selected ? (
              <>
                <div className="flex flex-wrap justify-end items-center gap-2">
                  {/* This tab is the READER's view now — the stories, and what
                      was held back as already reported. Everything about how
                      the run got here is one click away rather than stacked on
                      top of it, and the run controls live on Setup next to the
                      settings they test. */}
                  <button
                    onClick={() => setInspecting(selected.id)}
                    className="text-sm px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-indigo-400"
                  >
                    How this run went
                  </button>
                  <button
                    onClick={() => setChatOpen(true)}
                    className="text-sm px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-indigo-400"
                  >
                    Ask AI
                  </button>
                </div>
                <BriefingView
                  briefing={selected}
                  onInspect={() => setInspecting(selected.id)}
                />
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select a briefing.
              </p>
            )}
          </div>
        </div>
      )}

      {active === 'threads' && <ThreadsPanel beatId={id} reloadKey={liveKey} />}

      {/* One conversation, two tool sets. Which one it gets follows the tab it
          was opened from: settings on Setup, story corrections on a briefing.
          See jcseo/agents/news_beats_chat/tools.py for why the split runs that
          way round. */}
      <BeatAgentPane
        beatId={id}
        surface={active === 'briefings' ? 'comb' : 'tune'}
        briefingId={active === 'briefings' ? selected?.id ?? null : null}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        onChanged={() => {
          loadBriefings();
          if (selected) openBriefing(selected.id, { push: false });
          setVerdictKey((k) => k + 1);
        }}
        onSetupChanged={() => {
          loadBeat();
          setVerdictKey((k) => k + 1);
        }}
        seed={seed}
        onSeedConsumed={() => setSeed(null)}
      />

      {/* The whole account of one run, over the settings that produced it.
          Opened by either run trigger, by "How this run went" on a briefing,
          and by the pointer under a briefing's stories. Only one drawer at a
          time — they cover the same screen. */}
      <RunInspector
        beat={beat}
        briefingId={inspecting}
        open={inspecting !== null && !chatOpen}
        runState={runState}
        error={error}
        // Both counters only go up, so their sum moves whenever either does:
        // when a run lands (verdictKey) and while it files stories (liveKey).
        reloadKey={verdictKey + liveKey}
        onClose={() => setInspecting(null)}
        onBeatChanged={(next) => {
          setBeat(next);
          setVerdictKey((k) => k + 1);
        }}
        onReprocess={beat.mode === 'DRAFT' ? reprocess : undefined}
        onContinue={continueRun}
      />
    </div>
  );
}
